export type ProductAnalyticsEventLike = {
  user_id: string;
  event_name: string;
  path: string | null;
  metadata?: Record<string, unknown> | null;
};

export type ProductAnalyticsSummary = {
  activeUsers7d: number;
  pageViews7d: number;
  topRoutes: Array<{ path: string; count: number }>;
  keyEvents: Array<{ label: string; count: number }>;
  surfaceViews: Array<{ label: string; count: number }>;
};

const KNOWN_EVENT_LABELS: Record<string, string> = {
  community_post_created: 'Community posts created',
  data_export_downloaded: 'Data exports',
  debt_added: 'Debts added',
  life_check_completed: 'Life Checks completed',
  password_reset_requested: 'Password resets requested',
  statement_budget_applied: 'Budget suggestions applied',
  statement_export_downloaded: 'Statement exports',
  statement_expenses_synced: 'Statement expenses synced',
  statement_import_saved: 'Statements saved',
  survival_script_copied: 'Survival scripts copied',
};

export const EMPTY_PRODUCT_ANALYTICS_SUMMARY: ProductAnalyticsSummary = {
  activeUsers7d: 0,
  pageViews7d: 0,
  topRoutes: [],
  keyEvents: [],
  surfaceViews: [
    { label: 'Web views', count: 0 },
    { label: 'Native views', count: 0 },
  ],
};

function humaniseEventName(value: string) {
  return value
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function getEventSurface(event: ProductAnalyticsEventLike) {
  const metadataSurface = event.metadata?.surface;
  if (metadataSurface === 'native' || metadataSurface === 'web') {
    return metadataSurface;
  }

  return event.path?.startsWith('native:/') ? 'native' : 'web';
}

export function getProductEventLabel(eventName: string) {
  return KNOWN_EVENT_LABELS[eventName] || humaniseEventName(eventName);
}

export function buildProductAnalyticsSummary(events: ProductAnalyticsEventLike[]): ProductAnalyticsSummary {
  if (events.length === 0) {
    return EMPTY_PRODUCT_ANALYTICS_SUMMARY;
  }

  const pageViews = events.filter((event) => event.event_name === 'page_view');
  const routeCounts = new Map<string, number>();
  const eventCounts = new Map<string, number>();
  const surfaceCounts = new Map<string, number>([
    ['web', 0],
    ['native', 0],
  ]);

  for (const event of pageViews) {
    const path = event.path || 'Unknown';
    routeCounts.set(path, (routeCounts.get(path) || 0) + 1);

    const surface = getEventSurface(event);
    surfaceCounts.set(surface, (surfaceCounts.get(surface) || 0) + 1);
  }

  for (const event of events) {
    if (event.event_name === 'page_view') {
      continue;
    }
    eventCounts.set(event.event_name, (eventCounts.get(event.event_name) || 0) + 1);
  }

  return {
    activeUsers7d: new Set(events.map((event) => event.user_id)).size,
    pageViews7d: pageViews.length,
    topRoutes: Array.from(routeCounts.entries())
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
      .slice(0, 4)
      .map(([path, count]) => ({ path, count })),
    keyEvents: Array.from(eventCounts.entries())
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
      .slice(0, 5)
      .map(([eventName, count]) => ({ label: getProductEventLabel(eventName), count })),
    surfaceViews: [
      { label: 'Web views', count: surfaceCounts.get('web') || 0 },
      { label: 'Native views', count: surfaceCounts.get('native') || 0 },
    ],
  };
}
